import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import * as React from "react";

import { cn } from "../../lib/cn";
import { Input } from "../../primitive/input";

export interface DateInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  "type" | "value" | "onChange"
> {
  value: Dayjs | null | undefined;
  onChange: (value: Dayjs) => void;
}

dayjs.extend(customParseFormat);

type DatePart = "year" | "month" | "day";

const PARTS: DatePart[] = ["year", "month", "day"];
const PART_LENGTH = { year: 4, month: 2, day: 2 };
const PART_LABEL = { year: "year", month: "month", day: "day" };

function splitDate(date: string): Record<DatePart, string> {
  const [year = "", month = "", day = ""] = date.split("-");
  return { year, month, day };
}

function parseDateInput(parts: Record<DatePart, string>) {
  if (PARTS.some((part) => parts[part].length !== PART_LENGTH[part])) {
    return null;
  }
  const parsed = dayjs(`${parts.year}-${parts.month}-${parts.day}`, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.startOf("day") : null;
}

function DateInput({
  className,
  lang,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  onPaste,
  disabled,
  readOnly,
  ref,
  "aria-label": ariaLabel = "Date",
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...props
}: DateInputProps) {
  const [documentLang, setDocumentLang] = React.useState<string>();
  const effectiveLang = lang ?? documentLang;
  const selectedValue = value?.format("YYYY-MM-DD") ?? "";
  const [editing, setEditing] = React.useState(() => ({
    selected: selectedValue,
    draft: splitDate(selectedValue),
  }));

  // An external selection supersedes the local draft, without resetting
  // incomplete input on unrelated parent renders.
  if (editing.selected !== selectedValue) {
    setEditing({ selected: selectedValue, draft: splitDate(selectedValue) });
  }
  const draft = editing.selected === selectedValue ? editing.draft : splitDate(selectedValue);
  const inputs = React.useRef<Partial<Record<DatePart, HTMLInputElement | null>>>({});

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      setDocumentLang(document.documentElement.lang);
    }
  }, []);

  function updateDraft(next: Record<DatePart, string>) {
    setEditing({ selected: selectedValue, draft: next });
    const parsed = parseDateInput(next);
    if (parsed && parsed.format("YYYY-MM-DD") !== selectedValue) {
      onChange(parsed);
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (event.currentTarget.parentElement?.contains(event.relatedTarget)) {
      return;
    }
    const selected = splitDate(selectedValue);
    if (PARTS.some((part) => draft[part] !== selected[part])) {
      setEditing({ selected: selectedValue, draft: selected });
    }
    onBlur?.(event);
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      lang={effectiveLang}
      className={cn(
        "flex h-8 w-36 items-center rounded-md border border-input bg-transparent px-2 shadow-xs focus-within:border-ring focus-within:ring-[1px] focus-within:ring-ring/50",
        "has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-destructive/20",
        "has-[:disabled]:opacity-50",
        className,
      )}
    >
      {PARTS.map((part, index) => (
        <React.Fragment key={part}>
          {index > 0 ? <span aria-hidden="true" className="text-muted-foreground">-</span> : null}
          <Input
            {...(index === 0 ? props : {})}
            type="text"
            ref={index === 0 ? (node) => {
              inputs.current.year = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            } : (node) => { inputs.current[part] = node; }}
            aria-label={`${ariaLabel} ${PART_LABEL[part]}`}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            readOnly={readOnly}
            lang={effectiveLang}
            inputMode="numeric"
            maxLength={PART_LENGTH[part]}
            placeholder={part === "year" ? "yyyy" : part === "month" ? "mm" : "dd"}
            spellCheck={false}
            value={draft[part]}
            onChange={(event) => {
              const next = event.currentTarget.value;
              if (readOnly || next.length > PART_LENGTH[part] || !/^\d*$/.test(next)) return;
              updateDraft({ ...draft, [part]: next });
            }}
            onPaste={(event) => {
              if (readOnly) {
                onPaste?.(event);
                return;
              }
              const text = event.clipboardData.getData("text");
              if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
                event.preventDefault();
                updateDraft(splitDate(text));
              } else if (!/^\d*$/.test(text) || text.length > PART_LENGTH[part]) {
                event.preventDefault();
              } else {
                onPaste?.(event);
              }
            }}
            onFocus={(event) => {
              event.currentTarget.select();
              onFocus?.(event);
            }}
            onBlur={handleBlur}
            onKeyDown={(event) => {
              const position = PARTS.indexOf(part);
              if (event.key === "ArrowLeft" && position > 0 && event.currentTarget.selectionStart === 0) {
                event.preventDefault();
                inputs.current[PARTS[position - 1]]?.focus();
              } else if (event.key === "ArrowRight" && position < 2 && event.currentTarget.selectionStart === event.currentTarget.value.length) {
                event.preventDefault();
                inputs.current[PARTS[position + 1]]?.focus();
              } else if (event.key === "Backspace" && position > 0 && !event.currentTarget.value) {
                event.preventDefault();
                inputs.current[PARTS[position - 1]]?.focus();
              }
              onKeyDown?.(event);
            }}
            className={cn(
              "h-full min-w-0 rounded-none border-0 bg-transparent px-0 text-center shadow-none focus-visible:border-0 focus-visible:ring-0",
              part === "year" ? "w-10 flex-none" : "w-6 flex-none",
            )}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

export { DateInput };
